package top.wcpe.allincore.ui.runtime.internal.text

/**
 * 码点工具的平台中立实现：替代 `String.codePointAt` / `Character.charCount`
 * （java.* 不可进 commonMain，ADR-0072 R3 文本渲染件迁移的机械连带）。
 * 语义与 JDK 同名方法逐位一致：高低代理成对时合成增补码点，否则原样返回单元值。
 * `internal` 仅限 ui-runtime 模块内部使用。
 */

/** 增补平面起始码点（BMP 之外，需要代理对表示）。 */
private const val SUPPLEMENTARY_MIN = 0x10000

/** 高代理位段起始值。 */
private const val HIGH_SURROGATE_MIN = 0xD800

/** 低代理位段起始值。 */
private const val LOW_SURROGATE_MIN = 0xDC00

/** 高代理承载的高 10 位左移量。 */
private const val HIGH_SURROGATE_SHIFT = 10

/** 返回 [index] 处的 Unicode 码点；高代理后跟低代理时合成增补码点，语义同 `String.codePointAt`。 */
internal fun String.codePointAtCompat(index: Int): Int {
    val high = this[index]
    if (high.isHighSurrogate() && index + 1 < length) {
        val low = this[index + 1]
        if (low.isLowSurrogate()) {
            return ((high.code - HIGH_SURROGATE_MIN) shl HIGH_SURROGATE_SHIFT) +
                (low.code - LOW_SURROGATE_MIN) + SUPPLEMENTARY_MIN
        }
    }
    return high.code
}

/** 返回码点占用的 UTF-16 单元数（增补码点 2、其余 1），语义同 `Character.charCount`。 */
internal fun charCountOf(codePoint: Int): Int = if (codePoint >= SUPPLEMENTARY_MIN) 2 else 1

/** 统计 [beginIndex, endIndex) 内的码点数（成对代理算 1），语义同 `String.codePointCount`。 */
internal fun String.codePointCountCompat(beginIndex: Int, endIndex: Int): Int {
    if (beginIndex < 0 || endIndex > length || beginIndex > endIndex) throw IndexOutOfBoundsException()
    var count = 0
    var i = beginIndex
    while (i < endIndex) {
        val c = this[i]
        i += if (c.isHighSurrogate() && i + 1 < endIndex && this[i + 1].isLowSurrogate()) 2 else 1
        count++
    }
    return count
}

/**
 * 从 [index] 起正向步进 [codePointOffset] 个码点后的 UTF-16 下标，语义同 `String.offsetByCodePoints`。
 * 仅支持非负步进（现有调用面唯一形态）；越界抛 [IndexOutOfBoundsException]。
 */
internal fun String.offsetByCodePointsCompat(index: Int, codePointOffset: Int): Int {
    if (index < 0 || index > length) throw IndexOutOfBoundsException()
    require(codePointOffset >= 0) { "仅支持非负码点步进" }
    var i = index
    var remaining = codePointOffset
    while (remaining > 0) {
        if (i >= length) throw IndexOutOfBoundsException()
        val c = this[i]
        i += if (c.isHighSurrogate() && i + 1 < length && this[i + 1].isLowSurrogate()) 2 else 1
        remaining--
    }
    return i
}

/** 码点版空白判定，语义同 `Character.isWhitespace(int)`（增补平面无空白字符恒 false）。 */
internal fun isWhitespaceCodePoint(codePoint: Int): Boolean =
    codePoint in 0..0xFFFF && codePoint.toChar().isWhitespace()

/** ASCII 字母 / 数字判定（调用方保证 codePoint < 0x80 时语义同 `Character.isLetterOrDigit`）。 */
internal fun isAsciiLetterOrDigit(codePoint: Int): Boolean =
    codePoint in 'a'.code..'z'.code || codePoint in 'A'.code..'Z'.code || codePoint in '0'.code..'9'.code

/** 码点 → 字符串（增补码点拆代理对），语义同 `String(Character.toChars(cp))`。 */
internal fun codePointToString(codePoint: Int): String {
    if (codePoint < SUPPLEMENTARY_MIN) return codePoint.toChar().toString()
    val offset = codePoint - SUPPLEMENTARY_MIN
    val high = ((offset shr HIGH_SURROGATE_SHIFT) + HIGH_SURROGATE_MIN).toChar()
    val low = ((offset and 0x3FF) + LOW_SURROGATE_MIN).toChar()
    return charArrayOf(high, low).concatToString()
}
